import React, { useState } from 'react';
import clamp from 'lodash/clamp';
import isNumber from 'lodash/isNumber';

export interface StepDef<TExtras extends Record<string, unknown> = Record<string, unknown>> {
  id: string;
  title: string;
  element: React.ReactNode;
  extras?: TExtras;
}

export interface MultiStepController<TExtras extends Record<string, unknown> = Record<string, unknown>> {
  goToStep(stepId: string): void;

  goToStep(stepIndex: number): void;

  currentStepIndex: number;
  currentStep: StepDef<TExtras>;

  prevStep: StepDef<TExtras> | null;
  nextStep: StepDef<TExtras> | null;

  isFirstStep: boolean;
  isLastStep: boolean;

  canGoBack: boolean;
  goBack: () => void;

  canGoNext: boolean;
  goNext: () => void;
}

export function useMultiStepController<TExtras extends Record<string, unknown> = Record<string, unknown>>(props: {
  steps: StepDef<TExtras>[];
  initialStepIndex?: number;
}): MultiStepController<TExtras> {
  const { steps } = props;

  const minStepIndex = 0;
  const maxStepIndex = steps.length - 1;

  const [unclampedCurrentStepIndex, setCurrentStepIndex] = useState(props.initialStepIndex ?? 0);

  const currentStepIndex = clamp(unclampedCurrentStepIndex, minStepIndex, maxStepIndex);
  const currentStep = steps[currentStepIndex];

  const isFirstStep = currentStepIndex === minStepIndex;
  const isLastStep = currentStepIndex === maxStepIndex;

  const prevStepIndex = isFirstStep ? null : currentStepIndex - 1;
  const prevStep = prevStepIndex !== null ? steps[prevStepIndex] : null;

  const nextStepIndex = isLastStep ? null : currentStepIndex + 1;
  const nextStep = nextStepIndex !== null ? steps[nextStepIndex] : null;

  const canGoNext = !isLastStep;
  const canGoBack = !isFirstStep;

  function goToStep(stepId: string): void;
  function goToStep(stepIndex: number): void;
  function goToStep(stepIdOrIndex: string | number): void {
    if (isNumber(stepIdOrIndex)) {
      if (stepIdOrIndex < minStepIndex) {
        throw new Error('Cannot go to a step index less than ' + minStepIndex);
      }

      if (stepIdOrIndex > maxStepIndex) {
        throw new Error('Cannot go to a step higher than ' + maxStepIndex);
      }
    }

    const newStepIndex = isNumber(stepIdOrIndex) ? stepIdOrIndex : steps.findIndex((step) => step.id === stepIdOrIndex);

    if (newStepIndex === -1) {
      throw new Error('Step with ID ' + stepIdOrIndex + ' not found');
    }

    setCurrentStepIndex(newStepIndex);
  }

  const goNext = (): void => {
    if (canGoNext) {
      setCurrentStepIndex((prevState) => prevState + 1);
    }
  };

  const goBack = (): void => {
    if (canGoBack) {
      setCurrentStepIndex((prevState) => prevState - 1);
    }
  };

  return {
    goToStep,
    goNext,
    goBack,
    currentStep,
    prevStep,
    nextStep,
    currentStepIndex,
    canGoBack,
    canGoNext,
    isFirstStep,
    isLastStep,
  };
}
